import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { useQuery } from "react-query";
import {
  atomStore,
  daysmartAuthStateAtom,
  DAYSMART_RINKS,
  Rink,
  setSelectedMemberId,
  VendorAuthState,
  vendorAccountsAtom,
} from "../vendor";

const DAYSMART_COMPANY = "sharks";
const DAYSMART_SPORT_ID = 27;

export type Member = {
  key: string;
  firstName: string;
  lastName: string;
};

export type TimeSlot = {
  meetingKey: string;
  teamId: string;
  cost: number;
  durationMins: number;
  dateOnly: Date;
  dateTime: Date;
  currentEnrollment: number;
  rinkSurface?: string;
};

export type DaysmartRinkConfig = Rink & {
  facilityId: number;
  teamNamePrefix: string;
};

const RINK_META: Record<string, { facilityId: number; teamNamePrefix: string }> = {
  oakland: { facilityId: 3, teamNamePrefix: "OIC - 60 Minute Freestyle" },
  fremont: { facilityId: 2, teamNamePrefix: "FRE - Freestyle" },
};

export function daysmartRinkConfig(rink: Rink): DaysmartRinkConfig {
  const meta = RINK_META[rink.id];
  return {
    ...rink,
    facilityId: meta?.facilityId ?? 0,
    teamNamePrefix: meta?.teamNamePrefix ?? "",
  };
}

export type DaysmartAuthState = VendorAuthState & {
  token: string;
  customerId: string;
};

type ProxyArgs = {
  path: string;
  method?: string;
  body?: unknown;
  bearerToken?: string;
};

async function proxyRaw<T = unknown>(
  args: ProxyArgs
): Promise<{ status: number; body: T }> {
  const t0 = performance.now();
  const res = await fetch("/daysmart_proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  const parsed = (await res.json()) as { status: number; body: T };
  const ms = Math.round(performance.now() - t0);
  const path = args.path.split("?")[0];
  console.log(
    "[proxy]",
    args.method ?? "GET",
    path,
    "→",
    parsed.status,
    `(${ms}ms)`
  );
  return parsed;
}

async function proxy<T = unknown>(args: ProxyArgs): Promise<T> {
  const { status, body } = await proxyRaw<T>(args);
  if (status !== 200) {
    throw new Error(`daysmart ${status}: ${args.path.split("?")[0]}`);
  }
  return body;
}

export async function daysmartLogin(
  username: string,
  password: string
): Promise<DaysmartAuthState> {
  const { status, body } = await proxyRaw<{
    auth: boolean;
    user: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
    };
    access_token: string;
    expires_in: number;
    id: string;
  }>({
    path: `/dash/jsonapi/api/v1/customer/auth/token?company=${encodeURIComponent(
      DAYSMART_COMPANY
    )}`,
    method: "POST",
    body: {
      grant_type: "client_credentials",
      client_id: username,
      client_secret: password,
      stay_signed_in: false,
      company: DAYSMART_COMPANY,
      company_code: DAYSMART_COMPANY,
    },
  });
  if (status !== 200 || !body?.auth) {
    throw new Error("Auth error");
  }
  return {
    user: {
      firstName: body.user.first_name,
      lastName: body.user.last_name,
      email: body.user.email,
    },
    authedAt: new Date(),
    expiresAt: new Date(Date.now() + body.expires_in * 1000),
    token: body.access_token,
    customerId: String(body.user.id ?? body.id),
  };
}

export async function listMembers(
  auth: DaysmartAuthState
): Promise<Member[]> {
  const body = await proxy<{
    data: {
      id: string;
      attributes: { first_name: string; last_name: string };
      relationships: {
        children?: { data: Array<{ type: string; id: string }> };
        spouses?: { data: Array<{ type: string; id: string }> };
      };
    };
    included?: Array<{
      type: string;
      id: string;
      attributes: { first_name: string; last_name: string };
    }>;
  }>({
    path: `/dash/jsonapi/api/v1/customers/${auth.customerId}?include=spouses,children,parents&company=${encodeURIComponent(
      DAYSMART_COMPANY
    )}`,
    bearerToken: auth.token,
  });

  const members: Member[] = [
    {
      key: body.data.id,
      firstName: body.data.attributes.first_name,
      lastName: body.data.attributes.last_name,
    },
  ];
  const included = body.included ?? [];
  for (const rel of ["children", "spouses"] as const) {
    const relData = body.data.relationships[rel]?.data ?? [];
    for (const r of relData) {
      const inc = included.find((i) => i.type === r.type && i.id === r.id);
      if (inc) {
        members.push({
          key: inc.id,
          firstName: inc.attributes.first_name,
          lastName: inc.attributes.last_name,
        });
      }
    }
  }
  return members;
}

export async function fetchUpcomingSlots(
  rink: DaysmartRinkConfig,
  _auth: DaysmartAuthState
): Promise<TimeSlot[]> {
  const start = new Date();
  const end = new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const params = new URLSearchParams({
    company: DAYSMART_COMPANY,
    "page[size]": "200",
    sort: "end,start",
    "filter[start_date__gte]": fmt(start),
    "filter[start_date__lte]": fmt(end),
    "filter[unconstrained]": "1",
    "filter[homeTeam.sport_id__in]": String(DAYSMART_SPORT_ID),
    include: "summary,homeTeam,homeTeam.product,resource",
    "cache[save]": "false",
  });
  const body = await proxy<{
    data: Array<{
      id: string;
      attributes: { start: string; end: string };
      relationships: {
        homeTeam: { data: { type: string; id: string } | null };
        resource?: { data: { type: string; id: string } | null };
      };
    }>;
    included?: Array<{
      type: string;
      id: string;
      attributes: any;
      relationships?: any;
    }>;
  }>({
    path: `/dash/jsonapi/api/v1/events?${params.toString()}`,
  });

  const teamsById = new Map<
    string,
    { name: string; productId: string | null }
  >();
  const summariesById = new Map<
    string,
    { registered_count?: number; open_slots?: number }
  >();
  const productsById = new Map<string, { actual_price?: number }>();
  const resourcesById = new Map<string, { name: string }>();
  for (const inc of body.included ?? []) {
    if (inc.type === "teams") {
      teamsById.set(inc.id, {
        name: inc.attributes.name,
        productId: inc.relationships?.product?.data?.id ?? null,
      });
    } else if (inc.type === "event-summaries") {
      summariesById.set(inc.id, inc.attributes);
    } else if (inc.type === "products") {
      productsById.set(inc.id, inc.attributes);
    } else if (inc.type === "resources") {
      resourcesById.set(inc.id, { name: inc.attributes.name });
    }
  }

  const result: TimeSlot[] = [];
  for (const ev of body.data ?? []) {
    const teamId = ev.relationships.homeTeam.data?.id;
    const team = teamId ? teamsById.get(teamId) : null;
    if (!team || !team.name.startsWith(rink.teamNamePrefix)) continue;
    const summary = summariesById.get(ev.id);
    const product = team.productId ? productsById.get(team.productId) : null;
    const resourceId = ev.relationships.resource?.data?.id;
    const resource = resourceId ? resourcesById.get(resourceId) : null;
    const startDt = new Date(ev.attributes.start);
    const endDt = new Date(ev.attributes.end);
    const durationMins = Math.round(
      (endDt.getTime() - startDt.getTime()) / 60000
    );
    result.push({
      meetingKey: ev.id,
      teamId: teamId ?? "",
      cost: product?.actual_price ?? 0,
      durationMins,
      dateOnly: new Date(
        startDt.getFullYear(),
        startDt.getMonth(),
        startDt.getDate()
      ),
      dateTime: startDt,
      currentEnrollment: summary?.registered_count ?? 0,
      rinkSurface: resource?.name,
    });
  }
  return result.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
}

export const DAYSMART_CHECKOUT_URL =
  "https://apps.daysmartrecreation.com/dash/x/sharks/checkout";

export function addToCartUrl(slot: TimeSlot, company: string): string {
  const memberKey =
    atomStore.get(vendorAccountsAtom).daysmart?.selectedMemberId ?? null;
  const authState = atomStore.get(daysmartAuthStateAtom) as
    | DaysmartAuthState
    | null;
  const customerId = memberKey ?? authState?.customerId ?? "0";
  const params = new URLSearchParams({
    Action: "RegisterGroup/index",
    company,
    groupID: slot.teamId,
    customerID: customerId,
    "campEvents[]": slot.meetingKey,
    buttonAddToCart: "1",
    useAjax: "1",
  });
  return `https://apps.daysmartrecreation.com/dash/index.php?${params.toString()}`;
}

export async function fetchBookedMeetingKeys(
  auth: DaysmartAuthState
): Promise<string[]> {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().replace("T", " ").slice(0, 19);
  const params = new URLSearchParams({
    company: DAYSMART_COMPANY,
    sort: "start",
    "filter[start__gte]": fmt(now),
    include: "event",
  });
  const body = await proxy<{
    data: Array<{
      relationships: { event: { data: { type: string; id: string } | null } };
    }>;
  }>({
    path: `/dash/jsonapi/api/v1/customer-events?${params.toString()}`,
    bearerToken: auth.token,
  });
  return (body.data ?? [])
    .map((e) => e.relationships.event.data?.id)
    .filter((id): id is string => Boolean(id));
}

export function useSelectedMemberKey() {
  const authState = useAtomValue(daysmartAuthStateAtom) as
    | DaysmartAuthState
    | null;
  const accounts = useAtomValue(vendorAccountsAtom);
  const stored = accounts.daysmart?.selectedMemberId ?? null;
  const defaultKey = authState?.customerId ?? null;
  const memberKey = stored || defaultKey || null;

  const setMemberKey = useCallback((key: string | null) => {
    setSelectedMemberId("daysmart", key);
  }, []);

  return [memberKey, setMemberKey] as const;
}

export function useAllBookedMeetings() {
  const auth = useAtomValue(daysmartAuthStateAtom);
  return useQuery(
    ["allBookedMeetings", "daysmart", auth?.customerId],
    () => fetchBookedMeetingKeys(auth!),
    { enabled: !!auth, refetchOnWindowFocus: true }
  );
}

export function useMembers() {
  const auth = useAtomValue(daysmartAuthStateAtom);
  return useQuery(
    ["members", "daysmart", auth?.customerId],
    () => listMembers(auth!),
    { enabled: !!auth }
  );
}

export { DAYSMART_RINKS };
